import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { EmptyState } from "@/components/app/ui-bits";
import { CurrencyDollar, Plus, ArrowsClockwise, Scales } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const fmt = (v) => (v == null ? "—" : formatCurrency(v));

export default function FXCenterTab({ canEdit }) {
  const [rates, setRates] = useState([]);
  const [exposure, setExposure] = useState(null);
  const [revals, setRevals] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [revalOpen, setRevalOpen] = useState(false);

  const load = async () => {
    try {
      const [r, e, v] = await Promise.all([
        api.get("/finance/fx/rates"),
        api.get("/finance/fx/exposure"),
        api.get("/finance/fx/revaluations"),
      ]);
      setRates(r.data.items || []);
      setExposure(e.data);
      setRevals(v.data.items || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="fx-center-tab">
      {/* Exposure */}
      <div className="dc-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
          <div className="flex items-center gap-2">
            <Scales size={18} weight="fill" style={{ color: "#1E5CBA" }} />
            <h3 className="font-display text-[15px] font-semibold text-slate-900">Currency exposure — open AR/AP (base: USD)</h3>
          </div>
          <div className="flex items-center gap-3">
            {exposure && (
              <span className={`font-mono-alt text-xs font-semibold tabular-nums ${exposure.net_unrealized_usd >= 0 ? "text-emerald-700" : "text-rose-600"}`} data-testid="fx-net-unrealized">
                Net unrealized {fmt(exposure.net_unrealized_usd)}
              </span>
            )}
            {canEdit && (
              <Button size="sm" variant="outline" className="h-8" onClick={() => setRevalOpen(true)} data-testid="fx-revalue-btn">
                <ArrowsClockwise size={12} className="mr-1" /> Run revaluation
              </Button>
            )}
          </div>
        </div>
        {!exposure || exposure.items.length === 0 ? (
          <EmptyState icon={CurrencyDollar} title="No foreign-currency exposure" description="Foreign-currency AR invoices and AP bills will appear here with their unrealized FX position." />
        ) : (
          <div className="responsive-table-wrap">
            <table className="data-table w-full min-w-[1000px]">
              <thead>
                <tr>
                  <th>Currency</th><th className="text-right">Rate (per USD)</th>
                  <th className="text-right">AR open (FC)</th><th className="text-right">AR booked</th><th className="text-right">AR unrealized</th>
                  <th className="text-right">AP open (FC)</th><th className="text-right">AP booked</th><th className="text-right">AP unrealized</th>
                  <th className="text-right">Net</th>
                </tr>
              </thead>
              <tbody>
                {exposure.items.map((g) => (
                  <tr key={g.currency} className="text-sm" data-testid={`fx-exposure-${g.currency}`}>
                    <td className="px-5 py-3 font-mono-alt font-semibold">{g.currency}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{g.latest_rate ?? "—"}<div className="text-[10px] text-slate-400">{g.rate_date || ""}</div></td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{g.ar_fc.toLocaleString()}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{fmt(g.ar_booked_usd)}</td>
                    <td className={`px-5 py-3 text-right font-mono-alt tabular-nums font-semibold ${(g.ar_unrealized || 0) >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{fmt(g.ar_unrealized)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{g.ap_fc.toLocaleString()}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{fmt(g.ap_booked_usd)}</td>
                    <td className={`px-5 py-3 text-right font-mono-alt tabular-nums font-semibold ${(g.ap_unrealized || 0) >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{fmt(g.ap_unrealized)}</td>
                    <td className={`px-5 py-3 text-right font-mono-alt tabular-nums font-bold ${(g.net_unrealized || 0) >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{fmt(g.net_unrealized)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rate register */}
      <div className="dc-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-[15px] font-semibold text-slate-900">FX rate register</h3>
          {canEdit && (
            <Button size="sm" className="btn-primary-blue h-8" onClick={() => setAddOpen(true)} data-testid="fx-add-rate-btn">
              <Plus size={12} className="mr-1" /> Add rate
            </Button>
          )}
        </div>
        {rates.length === 0 ? (
          <EmptyState icon={CurrencyDollar} title="No rates yet" description="Record FX rates (foreign units per 1 USD) to enable foreign-currency invoicing." />
        ) : (
          <div className="responsive-table-wrap max-h-80 overflow-y-auto">
            <table className="data-table w-full min-w-[700px]">
              <thead><tr><th>Effective date</th><th>Currency</th><th className="text-right">Rate per USD</th><th>Source</th><th>Notes</th></tr></thead>
              <tbody>
                {rates.slice(0, 100).map((r) => (
                  <tr key={r.id} className="text-sm">
                    <td className="px-5 py-2.5 font-mono-alt text-xs tabular-nums">{formatDate(r.rate_date)}</td>
                    <td className="px-5 py-2.5 font-mono-alt font-semibold">{r.quote_currency}</td>
                    <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums">{r.rate}</td>
                    <td className="px-5 py-2.5 text-xs text-slate-600">{r.source}</td>
                    <td className="px-5 py-2.5 text-xs text-slate-500">{r.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Revaluation history */}
      <div className="dc-card overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Unrealized revaluation history</h3>
          <div className="text-[11px] text-slate-400">Each run posts a period-end journal (6921) plus an automatic reversal on the 1st of the next month.</div>
        </div>
        {revals.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-500">No revaluations run yet.</div>
        ) : (
          <div className="responsive-table-wrap">
            <table className="data-table w-full min-w-[800px]">
              <thead><tr><th>Period</th><th>Run at</th><th className="text-right">Net effect</th><th>Journal</th><th>Reversal</th><th>By</th></tr></thead>
              <tbody>
                {revals.map((v) => (
                  <tr key={v.id} className="text-sm" data-testid={`fx-reval-${v.period}`}>
                    <td className="px-5 py-2.5 font-mono-alt">{v.period}</td>
                    <td className="px-5 py-2.5 font-mono-alt text-xs">{formatDate(v.run_at)}</td>
                    <td className={`px-5 py-2.5 text-right font-mono-alt tabular-nums font-semibold ${v.net_unrealized >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{fmt(v.net_unrealized)}</td>
                    <td className="px-5 py-2.5 font-mono-alt text-xs">{v.entry_no}</td>
                    <td className="px-5 py-2.5 font-mono-alt text-xs">{v.reversal_entry_no} · {formatDate(v.reversal_date)}</td>
                    <td className="px-5 py-2.5 text-xs text-slate-500">{v.run_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddRateDialog open={addOpen} onClose={() => setAddOpen(false)} onDone={load} />
      <RevalueDialog open={revalOpen} onClose={() => setRevalOpen(false)} onDone={load} />
    </div>
  );
}

function AddRateDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState({ rate_date: new Date().toISOString().slice(0, 10), quote_currency: "", rate: "", source: "Central Bank", notes: "" });
  const submit = async () => {
    try {
      await api.post("/finance/fx/rates", { ...form, rate: parseFloat(form.rate || 0) });
      toast.success("Rate recorded");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add FX rate</DialogTitle>
          <DialogDescription>Rate = units of foreign currency per 1 USD (e.g. 0.92 EUR, 57000 SOS).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Effective date</Label><Input type="date" value={form.rate_date} onChange={(e) => setForm({ ...form, rate_date: e.target.value })} data-testid="fx-rate-date" /></div>
          <div><Label>Currency (3-letter)</Label><Input value={form.quote_currency} maxLength={3} placeholder="EUR" onChange={(e) => setForm({ ...form, quote_currency: e.target.value.toUpperCase() })} data-testid="fx-rate-currency" /></div>
          <div><Label>Rate per USD</Label><Input type="number" step="0.000001" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="fx-rate-value" /></div>
          <div><Label>Source</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></div>
          <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="btn-primary-blue" disabled={!form.quote_currency || !form.rate} data-testid="fx-rate-save">Save rate</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevalueDialog({ open, onClose, onDone }) {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [note, setNote] = useState("");
  const run = async () => {
    try {
      const { data } = await api.post("/finance/fx/revalue", { period, note });
      toast.success(`Revaluation posted · ${data.entry_no} (reversal ${data.reversal_entry_no || "—"})`);
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Run period-end FX revaluation</DialogTitle>
          <DialogDescription>Marks open FC balances to the latest rate. Posts an unrealized gain/loss journal at period end and an auto-reversal on the 1st of the following month.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div><Label>Period (YYYY-MM)</Label><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="fx-reval-period" /></div>
          <div><Label>Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" /></div>
        </div>
        <DialogFooter><Button onClick={run} className="btn-primary-blue" data-testid="fx-reval-run">Post revaluation</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
